import { DirtyFieldsType, Operation } from '@occc-app/offerHelpers/types';
import { makeDeleteOperation } from '@occc-app/offerHelpers/operations/makeDeleteOperation';
import {
  LimitCreateMutationVariables,
  LimitFragment,
  LimitUpdateMutationVariables,
  OfferFragment,
} from '@occc/graphql';
import { isFieldDirty } from '@occc-app/offerHelpers/utils';
import { limitHelper } from '@occc-app/offerHelpers/utils/limitHelper';

import { LimitsInConstraintGroupFormValue } from '../types';

const getLimitCreateOperation = (
  offerId: string,
  limit: number,
): Operation<'limitCreate', LimitCreateMutationVariables> => {
  return {
    name: 'limitCreate',
    variables: {
      input: {
        offerId,
        configuration: {
          inTransactionProgress: {
            limit,
          },
        },
      },
    },
  };
};

const getLimitUpdateOperation = (
  id: string,
  limit: number,
): Operation<'limitUpdate', LimitUpdateMutationVariables> => {
  return {
    name: 'limitUpdate',
    variables: {
      input: {
        id,
        configuration: {
          inTransactionProgress: {
            limit,
          },
        },
      },
    },
  };
};

export const getProgressPerTransactionLimitOperations = (
  offerId: string,
  values: Pick<LimitsInConstraintGroupFormValue, 'isEnabled' | 'progressPerTransaction'>,
  dirtyFields: DirtyFieldsType,
): Operation[] => {
  if (
    (!values.isEnabled || !values.progressPerTransaction?.isEnabled) &&
    values.progressPerTransaction?.id
  ) {
    return [makeDeleteOperation('limitDelete', values.progressPerTransaction.id)];
  }

  if (!values.isEnabled) {
    return [];
  }

  if (
    !values.progressPerTransaction?.id &&
    values.progressPerTransaction?.isEnabled &&
    typeof values.progressPerTransaction.limit === 'number'
  ) {
    return [getLimitCreateOperation(offerId, values.progressPerTransaction.limit)];
  }

  const isLimitInputDirty = isFieldDirty<{
    awardCount: LimitsInConstraintGroupFormValue;
  }>(dirtyFields, 'awardCount.progressPerTransaction.limit');

  if (
    values.progressPerTransaction?.id &&
    values.progressPerTransaction?.isEnabled &&
    typeof values.progressPerTransaction.limit === 'number' &&
    isLimitInputDirty
  ) {
    return [
      getLimitUpdateOperation(
        values.progressPerTransaction.id,
        values.progressPerTransaction.limit,
      ),
    ];
  }

  return [];
};

export const hasProgressPerTransactionLimit = (limits: LimitFragment[]) => {
  return limits.some(limitHelper.isInTransactionProgress);
};

export const findProgressPerTransactionLimit = (limits: LimitFragment[]) => {
  return limits.find(limitHelper.isInTransactionProgress);
};

export const getProgressPerTransactionLimitInitialValues = (
  offer: OfferFragment,
): LimitsInConstraintGroupFormValue['progressPerTransaction'] => {
  const limit = offer.limits.find(limitHelper.isInTransactionProgress);

  if (limit) {
    return {
      isEnabled: true,
      id: limit.id,
      limit: limit.limit,
    };
  }

  return {
    isEnabled: false,
  };
};
